import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useFirebaseAuthReady } from '../hooks/useFirebaseAuthReady';
import { OrgProvider, useOrg } from '../context/OrgContext';
import { isInternalAdmin, isOrgAdmin } from '../utils/roles';
import { AppLayout } from './AppLayout';
import { AppLoadingScreen } from './AppLoadingScreen';
import { ViewFadeIn } from './ViewFadeIn';
import { HomeView } from '../views/HomeView';
import { CreateOrgView } from '../views/CreateOrgView';

const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const SignupPage = lazy(() => import('../pages/auth/SignupPage'));
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage'));
const ConfirmPasswordPage = lazy(() => import('../pages/auth/ConfirmPasswordPage'));
const CustomersView = lazy(() => import('../views/CustomersView'));
const DocumentsView = lazy(() => import('../views/DocumentsView'));
const CasesView = lazy(() => import('../views/CasesView'));
const CaseWizardView = lazy(() => import('../views/CaseWizardView'));
const InstallmentsView = lazy(() => import('../views/InstallmentsView'));
const BrandingView = lazy(() => import('../views/BrandingView'));
const SettingsView = lazy(() => import('../views/SettingsView'));
const AuditLogView = lazy(() => import('../views/AuditLogView'));
const DocsView = lazy(() => import('../views/DocsView'));
const ChangelogView = lazy(() => import('../views/ChangelogView'));
const PublicCheckoutView = lazy(() => import('../views/checkout/PublicCheckoutView'));
const FinancingCheckPage = lazy(() => import('../pages/lead/FinancingCheckPage'));
const ClientsView = lazy(() => import('../views/ClientsView'));
const LendersView = lazy(() => import('../views/LendersView'));
const FinancingOptionsView = lazy(() => import('../views/FinancingOptionsView'));
const TeamView = lazy(() => import('../views/TeamView'));
const InviteView = lazy(() => import('../views/InviteView'));

function LoadingFallback({ variant = 'route' }: { variant?: 'auth' | 'route' | 'workspace' }) {
  return <AppLoadingScreen variant={variant} />;
}

/** Workspace management is admin-only; client_user (e.g. lead-created end
 * customers) is redirected to their overview. */
function OrgAdminRoute({ children }: { children: React.ReactNode }) {
  const { role } = useUser();
  if (!isOrgAdmin(role)) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}

function InternalAdminGate() {
  return (
    <Suspense fallback={<LoadingFallback variant="route" />}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Navigate to="/clients" replace />} />
          <Route path="/clients" element={<ClientsView />} />
          <Route path="/lenders" element={<LendersView />} />
          <Route path="/financing-options" element={<FinancingOptionsView />} />
          <Route path="/team" element={<TeamView />} />
          <Route path="/settings" element={<SettingsView />} />
          <Route path="/docs" element={<DocsView />} />
          <Route path="/changelog" element={<ChangelogView />} />
        </Route>
        <Route path="*" element={<Navigate to="/clients" replace />} />
      </Routes>
    </Suspense>
  );
}

/** Lender staff have no client organization — Team is their workspace. */
function LenderGate() {
  return (
    <Suspense fallback={<LoadingFallback variant="route" />}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Navigate to="/team" replace />} />
          <Route path="/team" element={<TeamView />} />
          <Route path="/docs" element={<DocsView />} />
          <Route path="/changelog" element={<ChangelogView />} />
        </Route>
        <Route path="*" element={<Navigate to="/team" replace />} />
      </Routes>
    </Suspense>
  );
}

function OrgGate() {
  const { org, loading } = useOrg();

  if (loading) {
    return <LoadingFallback variant="workspace" />;
  }

  if (!org) {
    return (
      <ViewFadeIn>
        <CreateOrgView />
      </ViewFadeIn>
    );
  }

  return (
    <Suspense fallback={<LoadingFallback variant="route" />}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<HomeView />} />
          <Route path="/home" element={<HomeView />} />
          <Route path="/customers" element={<OrgAdminRoute><CustomersView /></OrgAdminRoute>} />
          <Route path="/documents" element={<OrgAdminRoute><DocumentsView /></OrgAdminRoute>} />
          <Route path="/cases" element={<OrgAdminRoute><CasesView /></OrgAdminRoute>} />
          <Route path="/cases/new" element={<OrgAdminRoute><CaseWizardView /></OrgAdminRoute>} />
          <Route path="/installments" element={<OrgAdminRoute><InstallmentsView /></OrgAdminRoute>} />
          <Route path="/branding" element={<OrgAdminRoute><BrandingView /></OrgAdminRoute>} />
          <Route path="/team" element={<TeamView />} />
          <Route path="/settings" element={<OrgAdminRoute><SettingsView /></OrgAdminRoute>} />
          <Route path="/audit" element={<OrgAdminRoute><AuditLogView /></OrgAdminRoute>} />
          <Route path="/docs" element={<DocsView />} />
          <Route path="/changelog" element={<ChangelogView />} />
        </Route>
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </Suspense>
  );
}

export function AuthGate() {
  const { user, loading, role } = useUser();
  const isAuthReady = useFirebaseAuthReady();

  if (!isAuthReady || loading) {
    return <LoadingFallback variant="auth" />;
  }

  // Public checkout — no auth required
  return (
    <Suspense fallback={<LoadingFallback variant="auth" />}>
      <Routes>
        <Route path="/c/:publicToken" element={<PublicCheckoutView />} />
        <Route path="/c/:publicToken/*" element={<PublicCheckoutView />} />
        {/* Lead-gen financing check — public, works signed out and signed in */}
        <Route path="/check" element={<FinancingCheckPage />} />
        {/* Invitation confirm view — works signed out and signed in */}
        <Route path="/invite/:token" element={<InviteView />} />
        <Route
          path="*"
          element={
            !user ? (
              <ViewFadeIn>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/confirm-password" element={<ConfirmPasswordPage />} />
                  <Route path="/changelog" element={<ChangelogView />} />
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
              </ViewFadeIn>
            ) : isInternalAdmin(role) ? (
              <OrgProvider>
                <InternalAdminGate />
              </OrgProvider>
            ) : role === 'lender_admin' ? (
              <OrgProvider>
                <LenderGate />
              </OrgProvider>
            ) : (
              <OrgProvider>
                <OrgGate />
              </OrgProvider>
            )
          }
        />
      </Routes>
    </Suspense>
  );
}
